import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { API } from "@/lib/api";
import { formatUSD, formatDate, cleanOperatorName, formatLocal } from "@/lib/utils";
import { Download, Search, X, Loader2, FileText, BarChart3, CalendarDays } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import StatusPill from "@/components/StatusPill";
import ReportsCharts from "@/components/ReportsCharts";
import TransactionRowActions from "@/components/TransactionRowActions";

// Tabs that respect the global date range filter (others are intrinsically all-time).
const DATE_FILTERED_TABS = new Set(["sales", "master-agents", "commissions"]);

function _rangeToBoundaries(range, customFrom, customTo) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (range === "all") return [null, null];
  if (range === "yesterday") return [new Date(today.getTime() - 86400e3), today];
  if (range === "7d") return [new Date(today.getTime() - 7 * 86400e3), new Date(today.getTime() + 86400e3)];
  if (range === "30d") return [new Date(today.getTime() - 30 * 86400e3), new Date(today.getTime() + 86400e3)];
  if (range === "month") return [new Date(now.getFullYear(), now.getMonth(), 1), new Date(today.getTime() + 86400e3)];
  if (range === "last_month") return [
    new Date(now.getFullYear(), now.getMonth() - 1, 1),
    new Date(now.getFullYear(), now.getMonth(), 1),
  ];
  if (range === "custom" && customFrom && customTo) {
    return [new Date(customFrom + "T00:00:00Z"), new Date(customTo + "T23:59:59Z")];
  }
  return [today, new Date(today.getTime() + 86400e3)];
}

export default function Reports() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const TABS = useMemo(() => ([
    { id: "charts", label: t("reports.tab_charts", "Charts") },
    { id: "sales", label: t("reports.tab_sales", "Sales by status") },
    { id: "master-agents", label: t("reports.tab_master_agents", "By master agent"), adminOnly: true },
    { id: "agents", label: t("reports.tab_agents", "By agent"), managerOnly: true },
    { id: "operators", label: t("reports.tab_operators", "By operator") },
    { id: "countries", label: t("reports.tab_countries", "By country") },
    { id: "commissions", label: t("reports.tab_commissions", "Commissions") },
    { id: "search", label: t("reports.tab_search", "Search transactions") },
  ]), [t]);
  const RANGE_OPTIONS = useMemo(() => ([
    { key: "today", label: t("dashboard.today") },
    { key: "yesterday", label: t("dashboard.range_yesterday") },
    { key: "7d", label: t("dashboard.range_7d") },
    { key: "30d", label: t("dashboard.range_30d") },
    { key: "month", label: t("dashboard.month") },
    { key: "last_month", label: t("dashboard.range_last_month") },
    { key: "all", label: t("reports.range_all", "All time") },
  ]), [t]);
  const [tab, setTab] = useState("charts");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState("30d");
  const [customFrom, setCustomFrom] = useState(() => new Date(Date.now() - 30 * 86400e3).toISOString().slice(0, 10));
  const [customTo, setCustomTo] = useState(() => new Date().toISOString().slice(0, 10));

  const isAdmin = user.role === "MASTER_ADMIN";
  const isManager = isAdmin || user.role === "MASTER_AGENT";
  const visibleTabs = TABS.filter((t) => {
    if (t.adminOnly && !isAdmin) return false;
    if (t.managerOnly && !isManager) return false;
    return true;
  });

  // Build {start, end} param object — only when the current tab uses date filtering.
  const dateParams = useMemo(() => {
    const [s, e] = _rangeToBoundaries(range, customFrom, customTo);
    const out = {};
    if (s) out.start = s.toISOString();
    if (e) out.end = e.toISOString();
    return out;
  }, [range, customFrom, customTo]);

  const load = async (id) => {
    if (id === "search") return; // handled by SearchTab
    setLoading(true);
    try {
      const params = DATE_FILTERED_TABS.has(id) ? dateParams : {};
      const { data } = await api.get(`/reports/${id}`, { params });
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab !== "search" && tab !== "charts") load(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, range, customFrom, customTo]);

  const exportCsv = async (kind) => {
    const token = localStorage.getItem("token");
    const qs = new URLSearchParams({ kind });
    if (DATE_FILTERED_TABS.has(kind) || kind === "topups" || kind === "ledger") {
      if (dateParams.start) qs.set("start", dateParams.start);
      if (dateParams.end) qs.set("end", dateParams.end);
    }
    const res = await fetch(`${API}/reports/export.csv?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `report-${kind}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t("nav.reports")}</div>
          <h1
            className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {t("nav.reports")}
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="export-topups-csv"
            onClick={() => exportCsv("topups")}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
          >
            <Download className="w-4 h-4" /> {t("nav.send_topup")} CSV
          </button>
          <button
            data-testid="export-ledger-csv"
            onClick={() => exportCsv("ledger")}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
          >
            <Download className="w-4 h-4" /> {t("wallet.transactions_tab")} CSV
          </button>
          {isAdmin && (
            <button
              data-testid="export-master-agents-csv"
              onClick={() => exportCsv("master-agents")}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
            >
              <Download className="w-4 h-4" /> {t("role.MASTER_AGENT")} CSV
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {DATE_FILTERED_TABS.has(tab) && (
          <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 bg-slate-50/60 border-b border-slate-200" data-testid="report-date-filter-bar">
            <CalendarDays className="w-4 h-4 text-slate-500" />
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500 mr-1">{t("dashboard.custom")}</span>
            <select
              data-testid="report-range-select"
              value={range}
              onChange={(e) => setRange(e.target.value)}
              className="h-8 px-2 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
            >
              {RANGE_OPTIONS.map((o) => (<option key={o.key} value={o.key}>{o.label}</option>))}
              <option value="custom">{t("dashboard.custom")}</option>
            </select>
            {range === "custom" && (
              <>
                <input
                  type="date"
                  data-testid="report-custom-from"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="h-8 px-2 rounded-md border border-slate-300 bg-white text-sm"
                />
                <span className="text-slate-400 text-xs">→</span>
                <input
                  type="date"
                  data-testid="report-custom-to"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="h-8 px-2 rounded-md border border-slate-300 bg-white text-sm"
                />
              </>
            )}
          </div>
        )}
        <div className="flex flex-wrap border-b border-slate-200">
          {visibleTabs.map((t) => (
            <button
              key={t.id}
              data-testid={`report-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={
                "px-5 py-3 text-sm font-semibold border-b-2 transition-colors " +
                (tab === t.id
                  ? "text-[#0F62FE] border-[#0F62FE]"
                  : "text-slate-500 border-transparent hover:text-slate-700")
              }
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto">
          {tab === "search" ? (
            <SearchTab />
          ) : tab === "commissions" ? (
            <CommissionsTab user={user} />
          ) : tab === "charts" ? (
            <ReportsCharts />
          ) : (
            <ReportTable tab={tab} data={data} loading={loading} />
          )}
        </div>
      </div>
    </div>
  );
}

function ReportTable({ tab, data, loading }) {
  const { t } = useTranslation();
  if (loading) {
    return <div className="px-5 py-10 text-center text-sm text-slate-500">{t("reports.loading")}</div>;
  }
  if (!data?.length) {
    return <div className="px-5 py-10 text-center text-sm text-slate-500">{t("reports.no_data")}</div>;
  }

  const cols = {
    sales: [
      { k: "status", label: t("reports.col_status") },
      { k: "count", label: t("reports.col_count") },
      { k: "total_usd", label: t("reports.col_total"), money: true },
      { k: "commission_usd", label: t("reports.col_commission"), money: true },
    ],
    "master-agents": [
      { k: "agent_code", label: t("reports.col_id") },
      { k: "username", label: t("reports.col_master_agent") },
      { k: "agents_count", label: t("reports.col_agents_count") },
      { k: "count", label: t("reports.col_topups") },
      { k: "sales_total_usd", label: t("reports.col_network_sales"), money: true },
      { k: "commission_total_usd", label: t("reports.col_commissions_paid"), money: true },
    ],
    agents: [
      { k: "username", label: t("reports.col_agent") },
      { k: "count", label: t("reports.col_topups") },
      { k: "total_usd", label: t("reports.col_total_sales"), money: true },
      { k: "commission_usd", label: t("reports.col_commission"), money: true },
    ],
    operators: [
      { k: "name", label: t("reports.col_operator") },
      { k: "count", label: t("reports.col_topups") },
      { k: "total_usd", label: t("reports.col_total"), money: true },
    ],
    countries: [
      { k: "country_code", label: t("reports.col_country") },
      { k: "count", label: t("reports.col_topups") },
      { k: "total_usd", label: t("reports.col_total"), money: true },
    ],
  }[tab];

  return (
    <table className="w-full text-sm">
      <thead>
        <tr>
          {cols.map((c) => (
            <th
              key={c.k}
              className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-3 border-b border-slate-200"
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row, idx) => (
          <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
            {cols.map((c) => (
              <td key={c.k} className="px-5 py-3 text-slate-800">
                {c.money ? formatUSD(row[c.k] || 0) : row[c.k]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CommissionsTab({ user }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/reports/commissions")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="px-5 py-10 text-center text-sm text-slate-500">{t("reports.loading")}</div>;
  if (!data) return <div className="px-5 py-10 text-center text-sm text-slate-500">{t("reports.no_data_short")}</div>;

  const isMaster = data.scope === "network";

  return (
    <div>
      {!isMaster && (
        <div className="p-5 border-b border-slate-200 bg-amber-50/50">
          <div className="text-xs font-semibold uppercase tracking-widest text-amber-700">
            {t("reports.lifetime_earnings")}
          </div>
          <div
            data-testid="commission-total-self"
            className="text-3xl font-bold text-amber-900 mt-1"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {formatUSD(data.total_usd || 0)}
          </div>
        </div>
      )}
      <table className="w-full text-sm" data-testid="commissions-table">
        <thead>
          <tr>
            {(isMaster ? [t("reports.col_agent"), t("reports.col_topups"), t("reports.total_earned")] : [t("wallet.col_date"), t("reports.col_topups"), t("reports.total_earned")]).map((h) => (
              <th
                key={h}
                className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-3 border-b border-slate-200"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.length === 0 && (
            <tr>
              <td colSpan={3} className="px-5 py-10 text-center text-sm text-slate-500">
                {t("reports.no_commission_earnings")}
              </td>
            </tr>
          )}
          {data.rows.map((r, idx) => (
            <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
              <td className="px-5 py-3 text-slate-800">{isMaster ? r.username : r.date}</td>
              <td className="px-5 py-3 text-slate-700">{r.count}</td>
              <td className="px-5 py-3 font-semibold text-amber-700">{formatUSD(r.total_usd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SearchTab() {
  const { t } = useTranslation();
  const [phone, setPhone] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const run = async (e) => {
    e?.preventDefault?.();
    if (!phone.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const { data } = await api.get(`/topups?phone=${encodeURIComponent(phone.trim())}&limit=200`);
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    setPhone("");
    setRows([]);
    setSearched(false);
  };

  return (
    <div>
      <form onSubmit={run} className="p-5 border-b border-slate-200 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            data-testid="transaction-search-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t("reports.search_by_phone")}
            className="w-full h-11 pl-9 pr-3 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent"
          />
        </div>
        <button
          data-testid="transaction-search-btn"
          type="submit"
          disabled={loading || !phone.trim()}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {t("common.search")}
        </button>
        {searched && (
          <button
            type="button"
            onClick={clear}
            className="inline-flex items-center gap-1 h-11 px-3 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50"
          >
            <X className="w-4 h-4" /> {t("common.clear")}
          </button>
        )}
      </form>
      {searched && (
        <table className="w-full text-sm" data-testid="transaction-search-results">
          <thead>
            <tr>
              {[t("wallet.col_date"), t("reports.col_agent"), t("reports.col_country"), t("reports.col_operator"), t("topup.phone"), t("reports.customer_paid"), t("reports.usd_internal"), t("reports.rate"), t("common.status"), t("common.actions")].map((h) => (
                <th
                  key={h}
                  className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-3 border-b border-slate-200"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={10} className="px-5 py-10 text-center text-sm text-slate-500">
                  {t("reports.no_transactions_for", { phone })}
                </td>
              </tr>
            )}
            {rows.map((t) => {
              const localCcy = (t.local_currency || "USD").toUpperCase();
              const paidLocal = t.customer_pays_local != null
                ? t.customer_pays_local
                : Number(t.retail_price_usd || 0) * Number(t.exchange_rate || 1);
              return (
                <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-5 py-3 text-slate-700">{formatDate(t.created_at)}</td>
                  <td className="px-5 py-3 text-slate-700">{t.agent_username}</td>
                  <td className="px-5 py-3 text-slate-700">{t.country_code}</td>
                  <td className="px-5 py-3 text-slate-700">{cleanOperatorName(t.operator_name)}</td>
                  <td className="px-5 py-3 text-slate-700 font-mono text-xs">{t.phone_number}</td>
                  <td className="px-5 py-3 font-semibold text-slate-900">{formatLocal(paidLocal, localCcy)}</td>
                  <td className="px-5 py-3 text-slate-500 text-xs">{formatUSD(t.retail_price_usd)}</td>
                  <td className="px-5 py-3 text-slate-500 text-xs">{localCcy === "USD" ? "—" : `${t.exchange_rate ?? 1}`}</td>
                  <td className="px-5 py-3"><StatusPill status={t.status} /></td>
                  <td className="px-5 py-3"><TransactionRowActions topup={t} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}


function ReceiptDownloadLink({ topupId }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/topups/${topupId}/receipt.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `receipt-${topupId.slice(0, 8).toUpperCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // silent
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      data-testid={`receipt-download-${topupId.slice(0, 8)}`}
      onClick={download}
      disabled={busy}
      className="inline-flex items-center gap-1 text-xs font-semibold text-[#0F62FE] hover:underline disabled:opacity-50"
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
      PDF
    </button>
  );
}
